/**
 * Shared secured-facility resolution.
 *
 * Neutral primitives only — no VAT or quote-part business rules.
 * Both financing strategies depend on this module; neither depends on the other.
 */

import { n } from '../math.js';

/**
 * Resolve facilities committed in the shared P&L inputs.
 *
 * - Land / construction eligibility follow the P&L bank base source.
 * - The dedicated construction facility is a shared P&L bank assumption.
 * - Unsecured / required amounts are never invented here; strategies compute those.
 *
 * @returns {{
 *   landSecured: number,
 *   constructionSecured: number,
 *   privateSecured: number,
 *   sharedBankFacility: number,
 * }}
 */
export function resolveSecuredFacilities(pnl) {
  const secured = Math.max(0, n(pnl.securedBankFacilities));
  const sharedBankFacility = pnl.bankBaseSource === 'totalDevCost' ? secured : 0;
  const landSecured = pnl.bankBaseSource === 'landCost' ? secured : 0;

  // A total-development-cost facility is one shared pool: it cannot be counted
  // once for land and again for construction. An explicit construction facility
  // is a separate commitment (or replaces a construction-based P&L amount).
  const constructionSecured = Math.max(
    0,
    n(pnl.securedConstructionFacility) +
      (pnl.bankBaseSource === 'constructionCost' ? secured : 0),
  );

  return {
    landSecured,
    constructionSecured,
    privateSecured: Math.max(0, n(pnl.privateFunding)),
    sharedBankFacility,
  };
}

/** Total secured bank commitment headroom (shared facility or land + construction). */
export function securedBankFacilityCap(facilities) {
  if (!facilities) return 0;
  return Math.max(
    0,
    n(facilities.sharedBankFacility) +
      n(facilities.landSecured) +
      n(facilities.constructionSecured),
  );
}
